import type { OriginType, Task, TaskRecurrence, TaskStatus, TaskVisibility } from '@doneverse/shared'
import { z } from 'zod'
import { dateFormat } from './formatters.js'

export type DeadlineUrgency = 'overdue' | 'urgent' | 'warning' | 'normal' | 'completed'

/**
 * Compute deadline urgency for row highlighting.
 * - completed → no highlight
 * - overdue   → deadline has passed (red)
 * - urgent    → within 2 days of deadline (red)
 * - warning   → 2-4 days from deadline (yellow)
 * - normal    → more than 4 days away
 */
export function getDeadlineUrgency(deadline: string, status: TaskStatus): DeadlineUrgency {
  if (status === 'completed' || status === 'cancelled') return 'completed'

  const today = new Date(new Date().toDateString())
  const deadlineDate = new Date(deadline + 'T00:00:00')
  const diffMs = deadlineDate.getTime() - today.getTime()
  const diffDays = diffMs / (1000 * 60 * 60 * 24)

  if (diffDays < 0) return 'overdue'
  if (diffDays <= 2) return 'urgent'
  if (diffDays <= 4) return 'warning'
  return 'normal'
}

/**
 * Rank open tasks by urgency so "top priority" means the same thing everywhere
 * (the tasks board's Top Priorities and the doer-home tasks tile). Drops
 * completed/cancelled tasks; orders overdue → urgent → high-priority → warning →
 * the rest. Callers slice to however many rows they show.
 */
export function rankTasksByUrgency(tasks: Task[]): Task[] {
  return tasks
    .filter((t) => t.status !== 'completed' && t.status !== 'cancelled')
    .map((t) => {
      const urgency = getDeadlineUrgency(t.deadline, t.status)
      let score = 10
      if (urgency === 'overdue') score = 100
      else if (urgency === 'urgent') score = 80
      else if (t.priority === 'high') score = 60
      else if (urgency === 'warning') score = 40
      return { task: t, score }
    })
    .sort((a, b) => b.score - a.score)
    .map((s) => s.task)
}

const STATUS_LABELS: Record<TaskStatus, string> = {
  not_started: 'Not Started',
  in_progress: 'In Progress',
  completed: 'Completed',
  blocked: 'Blocked',
  cancelled: 'Cancelled',
}

export function formatStatus(status: TaskStatus): string {
  return STATUS_LABELS[status] ?? status
}

export function formatSource(source: string): string {
  if (source === 'founder') return 'Founder'
  if (source === 'doer') return 'Doer'
  return 'System'
}

/**
 * Human label for a task's provenance (`origin_type`) — *what* created the task,
 * which is orthogonal to `source` (the actor's role). Surfaced as a flag on task
 * views so coach- and playbook-generated tasks are distinguishable from
 * hand-authored ones.
 */
const ORIGIN_LABELS: Record<OriginType, string> = {
  session: 'AI Coach',
  pipeline: 'Playbook',
  user: 'Manual',
  system: 'Automation',
  basecamp: 'Basecamp',
}

export function formatOrigin(originType: OriginType): string {
  return ORIGIN_LABELS[originType] ?? originType
}

export function formatDeadline(deadline: string): string {
  const date = new Date(deadline + 'T00:00:00')
  return dateFormat({ month: 'short', day: 'numeric' }).format(date)
}

export function formatPriority(priority: string): string {
  return priority.charAt(0).toUpperCase() + priority.slice(1)
}

export const RECURRENCE_LABELS: Record<TaskRecurrence, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
  custom: 'Custom',
}

export type RecurrenceFormValue = 'none' | TaskRecurrence

export const RECURRENCE_OPTIONS: { value: RecurrenceFormValue; label: string }[] = [
  { value: 'none', label: 'One-off' },
  ...(Object.keys(RECURRENCE_LABELS) as TaskRecurrence[]).map((value) => ({
    value,
    label: RECURRENCE_LABELS[value],
  })),
]

export function formatRecurrence(recurrence: TaskRecurrence | null, rule?: string | null): string {
  if (!recurrence) return 'One-off'
  if (recurrence === 'custom') return rule ? `Custom · ${rule}` : 'Custom'
  return RECURRENCE_LABELS[recurrence]
}

interface RecurrenceFormFragment {
  recurrence: RecurrenceFormValue
  recurrence_rule?: string
}

export function isRecurrenceRuleValid(v: RecurrenceFormFragment): boolean {
  return v.recurrence !== 'custom' || (v.recurrence_rule?.trim().length ?? 0) > 0
}

export const RECURRENCE_RULE_REFINE = {
  message: 'Describe the schedule',
  path: ['recurrence_rule'],
}

export function getCreateRecurrencePayload(values: RecurrenceFormFragment): {
  recurrence?: TaskRecurrence
  recurrence_rule?: string
} {
  if (values.recurrence === 'none') return {}
  if (values.recurrence === 'custom') {
    return { recurrence: 'custom', recurrence_rule: values.recurrence_rule?.trim() }
  }
  return { recurrence: values.recurrence }
}

export function getUpdateRecurrencePayload(values: RecurrenceFormFragment): {
  recurrence: TaskRecurrence | null
  recurrence_rule: string | null
} {
  if (values.recurrence === 'none') return { recurrence: null, recurrence_rule: null }
  if (values.recurrence === 'custom') {
    return { recurrence: 'custom', recurrence_rule: values.recurrence_rule?.trim() ?? '' }
  }
  return { recurrence: values.recurrence, recurrence_rule: null }
}

export const VISIBILITY_OPTIONS: { value: TaskVisibility; label: string }[] = [
  { value: 'founder_visible', label: 'Founder-visible' },
  { value: 'internal_only', label: 'Internal' },
]

/**
 * The sentinel a Select uses for "no list".
 *
 * Radix Select cannot hold `''` as an item value, and a task with no list is
 * an ordinary state rather than an unset one — every task in production is in
 * it — so it needs a value of its own rather than an absence.
 */
export const NO_LIST_VALUE = '__none__'

export const baseTaskFormSchema = z.object({
  title: z.string().min(1, 'Task name is required'),
  expected_outcome: z.string().optional(),
  description: z.string().optional(),
  priority: z.enum(['low', 'medium', 'high']),
  deadline: z.string().min(1, 'Due date is required'),
  recurrence: z.enum(['none', 'daily', 'weekly', 'monthly', 'custom']),
  recurrence_rule: z.string().optional(),
  visibility: z.enum(['founder_visible', 'internal_only']),
  /**
   * Label membership, carried in the form but **not** in the task payload:
   * these are rows in `task_labels`, not columns on `tasks`, so the submit
   * handler sends them separately via `applyTaskLabels`.
   */
  list_id: z.string().default(NO_LIST_VALUE),
  collection_ids: z.array(z.string()).default([]),
})

/**
 * Base fields plus the assignee pair. Creating and editing a task validate
 * identically, so they share one schema — a reworded message or a new
 * constraint can't land on only one of the two forms.
 */
export const taskFormWithAssigneesSchema = baseTaskFormSchema
  .extend({
    assignee: z.string().min(1, 'Assignee is required'),
    additional_assignees: z.array(z.string()).default([]),
  })
  .refine(isRecurrenceRuleValid, RECURRENCE_RULE_REFINE)

export type TaskFormWithAssigneesValues = z.infer<typeof taskFormWithAssigneesSchema>
